import React, { useCallback, useEffect, useState } from "react";
import Avatar from "../../components/ui/Avatar";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Clock,
  PauseCircle,
  HardDrive,
  Monitor,
  StopCircle,
  RefreshCw,
} from "lucide-react";

import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Toast from "../../components/ui/Toast";
import useToast from "../../hooks/useToast";
import { monitoringService } from "../../api/monitoringService";

import ScreenshotGallery from "./components/ScreenshotGallery";
import RecordingPlayer from "./components/RecordingPlayer";
import {
  CAPTURE_MODE_LABEL,
  SESSION_STATUS_STYLE,
  employeeName,
  formatBytes,
  formatDateTime,
  formatWorked,
} from "./utils/format";

const Metric = ({ icon: Icon, label, value, hint }) => (
  <div className="bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-dark-200 flex items-center justify-center">
        <Icon className="w-4 h-4 text-gray-600 dark:text-light-400" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-light-500">{label}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-light-100 truncate">
          {value}
        </p>
        {hint && (
          <p className="text-[11px] text-gray-400 dark:text-light-500">{hint}</p>
        )}
      </div>
    </div>
  </div>
);

const SessionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      setData(await monitoringService.getSessionDetail(id));
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Could not load this session",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const forceEnd = async () => {
    setEnding(true);
    try {
      await monitoringService.forceEndSession(id, "Ended by an administrator");
      showToast("Session ended", "success");
      fetch();
    } catch (err) {
      showToast(
        err?.response?.data?.message || "Could not end the session",
        "error",
      );
    } finally {
      setEnding(false);
    }
  };

  if (loading && !data) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-accent" />
        </div>
      </PageWrapper>
    );
  }

  if (!data?.session) {
    return (
      <PageWrapper title="Session not found">
        <button
          onClick={() => navigate("/management/monitoring")}
          className="text-sm text-primary-accent hover:underline"
        >
          Back to Monitoring
        </button>
        <Toast {...toast} onClose={hideToast} />
      </PageWrapper>
    );
  }

  const { session, recordings, screenshots, totals } = data;
  const emp = session.employeeId;
  const style = SESSION_STATUS_STYLE[session.status] ?? SESSION_STATUS_STYLE.ended;
  const isLive = session.status === "active" || session.status === "paused";
  const totalBytes =
    (totals?.recordedBytes || 0) + (totals?.screenshotBytes || 0);

  return (
    <PageWrapper>
      <button
        onClick={() => navigate("/management/monitoring")}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 dark:text-light-500 hover:text-primary-accent transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Monitoring
      </button>

      {/* Header */}
      <div className="mb-4 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <Avatar src={emp?.photo} name={emp?.fullNameEn || emp?.nickname} size="lg" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold text-gray-900 dark:text-light-100">
              {employeeName(emp)}
            </h1>
            <span
              className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${style.bg} ${style.color}`}
            >
              {style.label}
            </span>
            {session.overrideId && (
              <span
                className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-primary-accent/15 text-primary-accent"
                title="A per-employee capture override was in force"
              >
                override
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-light-500 mt-1">
            {session.date} · {formatDateTime(session.startedAt)} →{" "}
            {session.endedAt ? formatDateTime(session.endedAt) : "in progress"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetch}
            disabled={loading}
            className="p-2 rounded-lg border border-gray-200 dark:border-dark-200 text-gray-600 dark:text-light-400 hover:bg-gray-100 dark:hover:bg-dark-300 disabled:opacity-40 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isLive && (
            <Button
              size="sm"
              variant="danger"
              onClick={forceEnd}
              disabled={ending}
              className="rounded-lg"
            >
              <StopCircle className="w-4 h-4" />
              {ending ? "Ending…" : "Force end"}
            </Button>
          )}
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <Metric
          icon={Clock}
          label="Worked"
          value={formatWorked(session.workedSeconds)}
        />
        <Metric
          icon={PauseCircle}
          label="Paused"
          value={
            session.pausedSeconds ? formatWorked(session.pausedSeconds) : "—"
          }
          hint={`${session.pauses?.length ?? 0} pause(s)`}
        />
        <Metric
          icon={Monitor}
          label="Capture mode"
          value={CAPTURE_MODE_LABEL[session.captureMode] ?? "—"}
          hint={
            session.settingsVersion
              ? `settings v${session.settingsVersion}`
              : undefined
          }
        />
        <Metric
          icon={HardDrive}
          label="Stored"
          value={formatBytes(totalBytes)}
          hint={`${totals?.chunkCount ?? 0} video · ${totals?.screenshotCount ?? 0} stills`}
        />
      </div>

      {/* What the app was actually TOLD to do. Shown because the resolution
          setting is only a downscale cap — the real geometry differs, and that is
          what the chunks record. */}
      {session.appliedSettings && (
        <div className="mb-4 bg-white dark:bg-primary-card rounded-xl border border-gray-200 dark:border-dark-100 p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-light-100 mb-2">
            Settings applied at start
          </h2>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 dark:text-light-500">
            <span>
              Requested cap:{" "}
              <strong className="text-gray-700 dark:text-light-300">
                {session.appliedSettings.resolution ?? "—"}
              </strong>
            </span>
            <span>
              fps:{" "}
              <strong className="text-gray-700 dark:text-light-300">
                {session.appliedSettings.fps ?? "—"}
              </strong>
            </span>
            <span>
              chunk:{" "}
              <strong className="text-gray-700 dark:text-light-300">
                {session.appliedSettings.chunkDuration
                  ? `${session.appliedSettings.chunkDuration}s`
                  : "—"}
              </strong>
            </span>
            {recordings?.[0]?.width ? (
              <span>
                actual video:{" "}
                <strong className="text-gray-700 dark:text-light-300">
                  {recordings[0].width}×{recordings[0].height}
                  {recordings[0].fps ? ` @${Math.round(recordings[0].fps)}fps` : ""}
                </strong>
              </span>
            ) : null}
            {screenshots?.[0]?.width ? (
              <span>
                actual stills:{" "}
                <strong className="text-gray-700 dark:text-light-300">
                  {screenshots[0].width}×{screenshots[0].height}
                </strong>
              </span>
            ) : null}
          </div>
        </div>
      )}

      {session.recordingsPurgedAt && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-400">
          The media for this session has been deleted by the retention policy. The
          time record is kept.
        </div>
      )}

      <div className="space-y-4">
        {recordings?.length > 0 && <RecordingPlayer recordings={recordings} />}
        <ScreenshotGallery screenshots={screenshots} />
      </div>

      <Toast {...toast} onClose={hideToast} />
    </PageWrapper>
  );
};

export default SessionDetail;
